import React, { useEffect } from "react";
import {
    Alert, AlertTitle, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataImport from './components/DataImport';
import DataVisualizer from './components/graphs/DataVisualizer';
import { hideProcessing, showProcessing } from './reducer/ProcessingSlice';
import Header from './components/Header';
import CalculationContainer from './CalculationContainer';
import CalculationActions from './CalculationActions';
import SummaryContainer from './SummaryContainer';
import { updateStartStop } from './reducer/CalcSettingsSlice';
import { useAuth0 } from "@auth0/auth0-react";
import LoginButton from "./auth/LoginButton";
import LoadUser from "./auth/LoadUser";
import { updateToken } from "./reducer/AuthSlice";
import { loadAU } from "./reducer/ScansSlice";
import RawSurfaceContent from "./components/rawsurface/RawSurfaceContent";
import RawSurfaceSettings from "./components/rawsurface/RawSurfaceSettings";
import RawSurfaceToolbar from "./components/rawsurface/RawSurfaceToolbar";
import RawSurfaceSaveButton from "./components/rawsurface/RawSurfaceSaveButton";
import { tokens } from "./theme";

// Tab identifiers. Scan Analysis is the pre-existing view; Raw Surface Analysis is the new
// pre-circle-fit diagnostic tab.
const TAB_SCAN = 0;
const TAB_RAW_SURFACE = 1;

/**
 * Auth0 reports token failures as short machine codes ("consent_required", "login_required", ...)
 * that mean nothing to an operator. Map the ones this app can realistically hit to a plain
 * sentence saying what happened and which button actually fixes it - the two are not
 * interchangeable, e.g. Retry can never clear a consent prompt because the whole point of that
 * error is that it needs an interactive screen the silent call cannot show.
 */
const TOKEN_ERROR_MESSAGES = {
    consent_required: "This application needs your permission before it can access your scan data. Choose Sign In Again to review and approve it.",
    login_required: "Your session has expired. Choose Sign In Again to continue.",
    interaction_required: "Your session could not be renewed automatically. Choose Sign In Again to continue.",
    mfa_required: "Additional verification is required. Choose Sign In Again to complete it.",
    missing_refresh_token: "Your session could not be renewed automatically. Choose Sign In Again to continue.",
    timeout: "Verifying your session took too long. Check your network connection, then choose Retry.",
};

const DEFAULT_TOKEN_ERROR_MESSAGE = "Your session could not be verified. Choose Retry, or Sign In Again if the problem continues.";

const describeTokenError = (err) => TOKEN_ERROR_MESSAGES[err?.error] || DEFAULT_TOKEN_ERROR_MESSAGE;

export default function VisualizerContent(props) {
    const [reset, setReset] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_SCAN);
    const laserData = useSelector((state) => state.laserData);
    const auth = useSelector((state) => state.auth);
    const scans = useSelector((state) => state.scans);
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();
    const {isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const [showContent, setShowContent] = useState(false);
    const [tokenError, setTokenError] = useState(null);
    // Bumped by the retry button to re-run the token effect. isAuthenticated won't change on
    // its own after a failure, so without this there is nothing to re-trigger the fetch.
    const [tokenAttempt, setTokenAttempt] = useState(0);

    useEffect(() => {
        const getToken = async() => {
            try {
                let token = await getAccessTokenSilently();
                dispatch(updateToken({token: token}));
                setTokenError(null);
                setShowContent(true);
            } catch (err) {
                // getAccessTokenSilently rejects whenever silent renewal fails - most often
                // because renewing without refresh tokens goes through a hidden-iframe
                // /authorize?prompt=none call that needs the Auth0 session cookie in a
                // third-party context, which Firefox ETP and Safari ITP block.
                //
                // auth0-react re-throws but still repopulates the cached user in its finally
                // block, so isAuthenticated stays true and the header renders normally. Without
                // this catch the rejection is silent, showContent is never set, and the app sits
                // on <LoadUser/> indefinitely with no error and no way out but another refresh.
                console.error("Unable to acquire an access token", err);
                setTokenError(err);
            }
        }

        if(isAuthenticated && auth.token.length === 0) {
            getToken();
        } else if (!isAuthenticated) {
            dispatch(updateToken({token: ""}));
        }
    },[isAuthenticated, tokenAttempt]);

    const handleTokenRetry = () => {
        setTokenError(null);
        setTokenAttempt((attempt) => attempt + 1);
    }

    // Loaded once, here, rather than per-toolbar - both tabs share this same AU list/state
    // (see ScansSlice.js), independent of which tab happens to render first.
    useEffect(() => {
        if (auth.token && scans.auStatus === "idle") {
            dispatch(loadAU());
        }
    }, [auth.token, scans.auStatus, dispatch]);

    const onFileSelected = () => {
        setReset(true);
        dispatch(showProcessing({ message: "Loading Scan..." }));
    }

    const onUnload = () => {
        setReset(true);
    }

    const handleApply = () => {
        setReset(true);
    }

    const onFileInputLoaded = (data) => {
        dispatch(hideProcessing());
        setReset(false);
    }

    const handleApplied = () => {
        setReset(false);
    }

    const onRangeUpdated = (start, end) => {
        dispatch(updateStartStop({
            start: start.data.x,
            stop: end.data.x,
            dataStart: laserData.rawData[0].x,
            dataEnd: laserData.rawData[laserData.rawData.length - 1].x
        }));
    }

    const renderSplash = () => {
        return (
            <Box sx={{ padding: "50px", textAlign: "center" }}>
                <Typography variant='h6' gutterBottom>Select a Alignment Unit and Scan to view</Typography>
                <Typography variant="body2" color="text.secondary">
                    Pick an Alignment Unit and a scan above, or upload a new scan from the toolbar to get started.
                </Typography>
            </Box>
        )
    }

    /**
     * Shown when the access token could not be acquired. Retry covers the transient cases (a
     * timed-out or momentarily blocked silent-auth call); signing in again covers the ones a
     * retry can't fix on its own, where the Auth0 session itself needs re-establishing
     * interactively. Either way the operator has a way forward without reloading the page.
     */
    const renderTokenError = () => (
        <Box sx={{
            flex: "1 1 auto", minHeight: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <Alert
                severity="error"
                variant="outlined"
                sx={{
                    fontSize: "1rem",
                    maxWidth: "620px",
                    backgroundColor: "#000",
                    border: `1px solid ${tokens.brand}`,
                    color: "#fff",
                    // Alert lays its icon / message / action out as flex rows that MUI tops-aligns
                    // by default (the icon and action slots each carry their own padding and
                    // negative margin to achieve that). Centring has to be set on the root *and*
                    // on each slot, with those offsets zeroed, or the icon still rides high
                    // against the title line rather than centring on the block as a whole.
                    alignItems: "center",
                    "& .MuiAlert-icon": {
                        color: tokens.brand,
                        alignItems: "center",
                        padding: 0,
                        marginRight: "12px",
                    },
                    "& .MuiAlert-message": { padding: 0 },
                    "& .MuiAlert-action": {
                        alignItems: "center",
                        padding: "0 0 0 16px",
                        marginTop: 0,
                        marginRight: 0,
                    },
                }}
                action={
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={handleTokenRetry}
                        >Retry</Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={() => loginWithRedirect()}
                        >Sign In Again</Button>
                    </Stack>
                }
            >
                <AlertTitle>Could not complete sign in</AlertTitle>
                {describeTokenError(tokenError)}
                {/* The raw Auth0 code, kept small and dim rather than dropped entirely: it is the
                    one thing that makes a support call actionable, and nobody in the field is
                    going to open devtools to read the console.error we also log. */}
                {tokenError?.error &&
                    <Typography sx={{ marginTop: "6px", fontSize: "11px", color: tokens.muted }}>
                        Reference code: {tokenError.error}
                    </Typography>
                }
            </Alert>
        </Box>
    )

    const renderUnauthorized = () => (
        <Box sx={{
            flex: "1 1 auto", minHeight: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <Alert severity="error" variant="filled" sx={{ fontSize: "1rem" }}>
                You are not authorized to access this application.
            </Alert>
        </Box>
    )

    /**
     * Shared two-column shell: main pane on the left, settings rail on the right.
     *
     * Both tabs use this so the settings rail sits in the same place throughout. This flips the
     * original Scan Analysis layout, which had its controls on the left.
     *
     * Both columns stretch to the full row height and scroll independently (rather than the
     * whole page scrolling as one unit) - on a 1920x1080 monitor the rail's cards already run
     * past the fold, and more are coming once shim-value calculation lands. `railFooter`, when
     * given, is pinned to the bottom of the rail outside that scroll region (Scan Analysis's
     * Apply / Apply & Save) so it stays visible no matter how tall the rest of the rail gets.
     */
    /**
     * `railOverlay`, when given, covers the whole rail column (scrollable rail + pinned footer
     * together) - used for a busy state that isn't specific to one card in the rail and would
     * otherwise be easy to miss below the fold (e.g. Raw Surface's shim calculation, which used to
     * only show inside the Shim Values card, often scrolled out of view).
     */
    const renderWithRightRail = (main, rail, railFooter, railOverlay) => (
        // Grid rather than flex, adapted from the mockup's `.work` rule:
        //   grid-template-columns: minmax(0, 1fr) 300px; gap: 16px; padding: 16px 20px 24px
        // Widened to 350px (Kevin, 2026-08-13) - some rail cards felt cramped at the mockup's
        // original 300px. The minmax(0, 1fr) matters - without the 0 minimum, a wide chart
        // canvas refuses to shrink and pushes the rail off screen.
        <Box sx={{
            flex: "1 1 auto",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 350px" },
            gap: "16px",
            padding: "16px 20px 24px",
        }}>
            {/* minHeight: 0 on both grid cells - grid/flex items default to min-height: auto,
                which refuses to shrink below content size and defeats overflowY: auto entirely.
                Without this, the rail (and main) just grow past the viewport instead of
                scrolling, and the shell's overflow: hidden then clips them outright. */}
            <Box sx={{ minWidth: 0, minHeight: 0, height: "100%", overflowY: "auto" }}>{main}</Box>
            <Box sx={{ position: "relative", minHeight: 0, height: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
                <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>{rail}</Box>
                {railFooter && <Box sx={{ flex: "0 0 auto" }}>{railFooter}</Box>}
                {railOverlay && (
                    <Box sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 30,
                        borderRadius: `${tokens.radius}px`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: alpha(tokens.ink, 0.72),
                        backdropFilter: "blur(1.5px)",
                    }}>
                        <Stack alignItems="center" spacing={1.25}>
                            <CircularProgress size={26} thickness={3} color="secondary" />
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: tokens.text }}>
                                {railOverlay}
                            </Typography>
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );

    const renderScanAnalysis = () => {
        if (laserData.allValues.length === 0) {
            // No two-column split here - going through renderWithRightRail left an empty
            // rail column in place even with nothing to put in it, which pushed this text off
            // true page-center (centered within the narrower main column instead).
            return (
                <Box sx={{
                    flex: "1 1 auto", minHeight: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    {renderSplash()}
                </Box>
            );
        }

        return renderWithRightRail(
            <Stack>
                <SummaryContainer />
                <DataVisualizer
                    reset={reset}
                    onRangeUpdated={onRangeUpdated}
                />
            </Stack>,
            <CalculationContainer />,
            <CalculationActions onApply={handleApply} onApplied={handleApplied} />
        );
    };

    const renderRawSurfaceAnalysis = () => {
        // No right rail until a capture is actually loaded - RawSurfaceContent.js uses this
        // same circles.length signal for its own "is something loaded" splash vs. analysis
        // branch, so reusing it here keeps both in sync without a second source of truth.
        if (rawSurface.circles.length === 0) {
            return (
                <Box sx={{
                    flex: "1 1 auto", minHeight: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <RawSurfaceContent />
                </Box>
            );
        }

        return renderWithRightRail(
            <RawSurfaceContent />,
            <RawSurfaceSettings />,
            <RawSurfaceSaveButton />,
            rawSurface.shimStatus === "computing" ? "Calculating shim values…" : null
        );
    };

    const renderContent = () => {
        if(showContent) {
            if (scans.auStatus === "unauthorized") {
                return renderUnauthorized();
            }

            return (
                <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
                    {/* Tab strip styling (dark ink field, brand-red active underline) lives in
                        the shared theme's MuiTabs/MuiTab overrides so it cannot drift per-usage.
                        flexShrink: 0 - without it, this is a shrinkable flex item like any other
                        child of the column above, and when the column's total content overflows
                        its available height, the browser's flex-shrink distribution can land it
                        (and the indicator riding on it) a device pixel under its own min-height,
                        differing slightly by tab/selection state depending on how much the other
                        children below are overflowing. It should never shrink at all. */}
                    <Tabs
                        value={activeTab}
                        onChange={(e, value) => setActiveTab(value)}
                        sx={{ flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                    >
                        <Tab label="Scan Analysis" />
                        <Tab label="Raw Surface Analysis" />
                    </Tabs>

                    {/* Per-tab toolbar (AU/scan pickers, upload, delete) - a full-width row
                        directly under the tab strip, matching the mockup. Previously
                        DataImport/DataWatcher lived stacked in the right rail instead.
                        flexShrink: 0 for the same reason as the Tabs above - this is a fixed
                        header row, not part of the flexible/scrollable content below it. */}
                    <Box sx={{ flexShrink: 0, display: activeTab === TAB_SCAN ? "block" : "none" }}>
                        <DataImport
                            onFileSelected={onFileSelected}
                            onLoaded={onFileInputLoaded}
                            onUnload={onUnload}
                        />
                    </Box>
                    <Box sx={{ flexShrink: 0, display: activeTab === TAB_RAW_SURFACE ? "block" : "none" }}>
                        <RawSurfaceToolbar />
                    </Box>

                    {/*
                      Both tabs stay mounted and are hidden with CSS rather than unmounted.
                      Switching tabs would otherwise tear down the chart.js canvases and discard
                      a loaded capture, forcing a re-upload just to glance at the other view.
                      display:flex (not "block") when visible so the grid content below can fill
                      the remaining height via flex:1 rather than just its natural content size.
                    */}
                    <Box sx={{
                        flex: "1 1 auto", minHeight: 0, flexDirection: "column",
                        display: activeTab === TAB_SCAN ? "flex" : "none",
                    }}>
                        {renderScanAnalysis()}
                    </Box>
                    <Box sx={{
                        flex: "1 1 auto", minHeight: 0, flexDirection: "column",
                        display: activeTab === TAB_RAW_SURFACE ? "flex" : "none",
                    }}>
                        {renderRawSurfaceAnalysis()}
                    </Box>
                </Box>
            );
        } else if(tokenError) {
            // Checked before the isAuthenticated branch below: a failed token fetch leaves the
            // cached user in place, so isAuthenticated is still true here and would otherwise
            // route straight back to the indefinite <LoadUser/>.
            return renderTokenError();
        } else if(isLoading || isAuthenticated) {
            return(<Box><LoadUser/></Box>)
        } else {
            return(<Box><LoginButton /></Box>)
        }
    }

    return (
        <Box sx={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Header />
            {renderContent()}
        </Box>
    );
}
